import React, { useEffect, useState } from 'react';
import MatchChronicPanel from '../pages/gps/MatchChronicPanel';
import { fetchMatchChronicPanelData } from '../pages/gps/matchChronicShared';
import MedicalPanel from '../pages/MedicalPanel';
import { fetchMedicalPanelData } from '../pages/medicalShared';
import MaxSpeedPanel from '../pages/gps/MaxSpeedPanel';
import { fetchMaxSpeedPanelData } from '../pages/gps/maxSpeedShared';
import PlayerChronicPanel from '../pages/gps/PlayerChronicPanel';
import { fetchPlayerChronicPanelData } from '../pages/gps/playerChronicShared';
import IndividualTargetsPanel from '../pages/gps/IndividualTargetsPanel';
import { fetchIndividualTargetsPanelData, fetchIndividualTargetsSummaries } from '../pages/gps/individualTargetsShared';
import { SeeFullPageLink } from './EinsteinLinks';

// Real inline panel rendering for `compose` responses (2026-09-10) — the compose tier existed
// since Phase 1, but every element rendered as a plain "Open X page" link chip, for every
// registryKey, with no actual side-by-side content ever appearing in the chat itself; the one
// place a panel DID render inline was a detached demo route (/einstein-compose-test), never
// reachable from the real product. gps_match_chronic + medical closed that gap first; gps_max_speed/
// gps_player_chronic/gps_individual_targets followed the same day. gps_goalkeeper/gps_team_training/
// gps_rehab_training have no panel — they were actually miscategorized as composable at all (no
// player_id param, see services/einsteinRouter.js's COMPOSABLE_REGISTRY_KEYS comment) and were
// moved to the honest fallback bucket instead, so there's nothing to build a panel for there.
//
// fetchData receives `elements` (not just called with no args) because gps_individual_targets'
// target resolution is genuinely per-(player, week) — everything else ignores the argument.
export const PANEL_REGISTRY = {
  gps_match_chronic: { Component: MatchChronicPanel, fetchData: fetchMatchChronicPanelData },
  medical: { Component: MedicalPanel, fetchData: fetchMedicalPanelData },
  gps_max_speed: { Component: MaxSpeedPanel, fetchData: fetchMaxSpeedPanelData },
  gps_player_chronic: { Component: PlayerChronicPanel, fetchData: fetchPlayerChronicPanelData },
  gps_individual_targets: {
    Component: IndividualTargetsPanel,
    // Batches every element's (player_id, iso_week) target resolution into ONE
    // resolveTargetsBatch call (see individualTargetsShared.js's header comment for why a plain
    // session-id key isn't safe once multiple players share the same session) rather than one
    // round trip per panel.
    fetchData: async (elements) => {
      const { sessions, players } = await fetchIndividualTargetsPanelData();
      const requests = (elements || [])
        .map(el => ({ playerId: el.params?.player_id, isoWeek: el.params?.iso_week }))
        .filter(r => r.playerId && r.isoWeek);
      const summariesByRequest = await fetchIndividualTargetsSummaries(requests, sessions);
      return { players, summariesByRequest };
    },
  },
};

// One shared fetch per registryKey group, not per element — mirrors ComposeTestMatchChronic.js's
// "fetch once, render N panels" pattern. `elements` all share the same registryKey (the caller
// groups them before rendering this). Each panel gets the explicit `playerId` prop every panel
// component expects, PLUS the element's raw params spread in (snake_case, e.g. `iso_week`) for
// the one panel (IndividualTargetsPanel) that needs more than just which player.
export function ComposePanelGroup({ registryKey, elements, prettyKey, goToRoute, accent }) {
  const { Component, fetchData } = PANEL_REGISTRY[registryKey];
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchData(elements)
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registryKey]);

  if (failed) {
    return <div style={{ fontSize: 12.5, opacity: 0.7 }}>Couldn't load {prettyKey(registryKey)} data.</div>;
  }
  if (!data) {
    return <div style={{ fontSize: 12.5, opacity: 0.7 }}>Loading {prettyKey(registryKey)}…</div>;
  }
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      {elements.map((el, i) => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Component playerId={el.params?.player_id} {...el.params} {...data} />
          <SeeFullPageLink sourceRoute={el.route} sourceKey={el.registryKey} goToRoute={goToRoute} accent={accent} />
        </div>
      ))}
    </div>
  );
}
